import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Menu, X, LogOut, Shield, Upload as UploadIcon } from "lucide-react";
import { useState } from "react";

const links = [
  { to: "/", label: "HOME" },
  { to: "/artists", label: "ARTISTS" },
  { to: "/about", label: "ABOUT" },
  { to: "/gaming", label: "GAMING" },
  { to: "/gallery", label: "GALLERY" },
  { to: "/connect", label: "CONNECT" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.35)]" data-testid="main-nav">
      {/* Living aurora backdrop + drifting orbs (behind blur) */}
      <div className="absolute inset-0 -z-10 overflow-hidden bg-aurora" aria-hidden="true">
        <div className="orb orb-red orb-live" style={{ width: 260, height: 260, top: -140, left: "8%", opacity: 0.55 }} />
        <div className="orb orb-amber orb-live" style={{ width: 220, height: 220, top: -110, left: "48%", opacity: 0.5, animationDelay: "-5s" }} />
        <div className="orb orb-cyan orb-live" style={{ width: 240, height: 240, top: -130, right: "8%", opacity: 0.35, animationDelay: "-9s" }} />
      </div>
      {/* Translucent glass overlay to soften the aurora and keep text legible */}
      <div className="absolute inset-0 -z-10 bg-black/45 backdrop-blur-2xl" aria-hidden="true" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="font-display text-2xl tracking-tight">SYNDA<span className="text-[#E63946]">FAM</span></span>
          <span className="font-mono text-[10px] text-white/40 hidden sm:block">/ MUSIC GROUP</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `font-display text-sm tracking-wider transition-colors ${isActive ? "text-[#E63946]" : "text-white/80 hover:text-white"}`
              }
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              {user.role === "admin" && (
                <button
                  onClick={() => navigate("/admin")}
                  className="font-mono text-xs px-3 py-2 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white transition-colors flex items-center gap-1"
                  data-testid="nav-admin-btn"
                >
                  <Shield className="w-3.5 h-3.5" /> ADMIN
                </button>
              )}
              {(user.role === "creator" || user.role === "admin") && (
                <button
                  onClick={() => navigate("/creator")}
                  className="font-mono text-xs px-3 py-2 border border-[#FCA311] text-[#FCA311] hover:bg-[#FCA311] hover:text-black transition-colors flex items-center gap-1"
                  data-testid="nav-creator-btn"
                >
                  <UploadIcon className="w-3.5 h-3.5" /> UPLOAD
                </button>
              )}
              <button
                onClick={logout}
                className="p-2 hover:bg-white/5 transition-colors"
                data-testid="nav-logout-btn"
                aria-label="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate("/signin")}
              className="hidden sm:block font-mono text-xs px-4 py-2 border border-white/30 hover:bg-white hover:text-black transition-colors"
              data-testid="nav-login-btn"
            >
              SIGN IN
            </button>
          )}

          <button
            className="md:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/90" data-testid="mobile-menu">
          <div className="flex flex-col px-5 py-4 gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-display text-lg ${isActive ? "text-[#E63946]" : "text-white/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {!user && (
              <button
                onClick={() => { setOpen(false); navigate("/signin"); }}
                className="font-mono text-xs px-4 py-3 border border-white/30 mt-2 w-fit"
                data-testid="mobile-login-btn"
              >
                SIGN IN
              </button>
            )}
            {user?.role === "admin" && (
              <button
                onClick={() => { setOpen(false); navigate("/admin"); }}
                className="font-mono text-xs px-4 py-3 border border-[#E63946] text-[#E63946] mt-2 w-fit"
              >
                ADMIN
              </button>
            )}
            {(user?.role === "creator" || user?.role === "admin") && (
              <button
                onClick={() => { setOpen(false); navigate("/creator"); }}
                className="font-mono text-xs px-4 py-3 border border-[#FCA311] text-[#FCA311] mt-2 w-fit"
                data-testid="mobile-creator-btn"
              >
                UPLOAD
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
